import React, { useEffect, useState, useCallback } from 'react';
import { ArrowDownLeft, ArrowUpRight, Loader2, X, RotateCcw, CheckCircle, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { createDeposit, pollDeposit } from '../../services/pawapayService';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  COMPLETED: 'text-emerald-700 bg-emerald-50',
  ACCEPTED: 'text-amber-700 bg-amber-50',
  PENDING: 'text-amber-700 bg-amber-50',
  SUBMITTED: 'text-amber-700 bg-amber-50',
  IN_RECONCILIATION: 'text-amber-700 bg-amber-50',
  FAILED: 'text-red-700 bg-red-50',
  REJECTED: 'text-red-700 bg-red-50',
};
const FAILED_STATES = ['FAILED', 'REJECTED', 'TIMEOUT'];
const fmt = (v) => `MWK ${Number(v || 0).toLocaleString()}`;

const ReceiptRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
    <span className="text-[12px] text-gray-500">{label}</span>
    <span className="text-[12px] font-semibold text-gray-900 text-right break-all">{value}</span>
  </div>
);

const ReceiptModal = ({ item, onClose, onRetried }) => {
  const [retrying, setRetrying] = useState(false);
  const [retryStatus, setRetryStatus] = useState('');
  const isPayout = item.type === 'payout';
  const canRetry = !isPayout && FAILED_STATES.includes(item.status);

  const buildPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: [360, 520] });
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, 360, 70, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18); doc.setFont(undefined, 'bold');
    doc.text('Ride Yachangu', 24, 34);
    doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.text('Payment receipt', 24, 52);

    doc.setTextColor(17, 24, 39);
    doc.setFontSize(28); doc.setFont(undefined, 'bold');
    doc.text(fmt(item.amount), 24, 120);
    doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(`${isPayout ? 'Cash-out to mobile money' : 'Mobile money payment'} · ${item.status || ''}`, 24, 140);

    const rows = [
      ['Provider', item.provider === 'TNM_MWI' ? 'TNM Mpamba' : item.provider === 'AIRTEL_MWI' ? 'Airtel Money' : item.provider || '-'],
      ['Phone', item.phoneNumber || '-'],
      ['Date', item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'],
      ['Transaction ID', item.providerTransactionId || '-'],
      ['Reference', item.id || '-'],
      ...(item.ride_id ? [['Ride', item.ride_id]] : []),
    ];
    let y = 180;
    doc.setDrawColor(229, 231, 235);
    rows.forEach(([k, v]) => {
      doc.setTextColor(107, 114, 128); doc.setFontSize(10);
      doc.text(String(k), 24, y);
      doc.setTextColor(17, 24, 39); doc.setFont(undefined, 'bold');
      doc.text(doc.splitTextToSize(String(v), 200), 336, y, { align: 'right' });
      doc.setFont(undefined, 'normal');
      doc.line(24, y + 8, 336, y + 8);
      y += 30;
    });
    doc.setTextColor(156, 163, 175); doc.setFontSize(9);
    doc.text('Thank you for riding with Ride Yachangu', 24, y + 16);
    return doc;
  };

  const downloadPdf = () => {
    try { buildPdf().save(`yachangu-receipt-${(item.id || 'payment').slice(0, 8)}.pdf`); } catch (_) { /* ignore */ }
  };

  const sharePdf = async () => {
    try {
      const blob = buildPdf().output('blob');
      const file = new File([blob], 'yachangu-receipt.pdf', { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Yachangu receipt', text: `Payment of ${fmt(item.amount)}` });
      } else {
        downloadPdf();
      }
    } catch (_) { /* user dismissed */ }
  };

  const retry = async () => {
    setRetrying(true);
    setRetryStatus('Requesting payment…');
    try {
      const dep = await createDeposit({
        amount: item.amount,
        phoneNumber: item.phoneNumber,
        provider: item.provider,
        rideId: item.ride_id,
        description: item.description || 'Retry payment',
      });
      setRetryStatus('Approve the prompt on your phone…');
      const final = await pollDeposit(dep.depositId);
      setRetryStatus(final === 'COMPLETED' ? 'Payment received 🎉' : final === 'FAILED' ? 'Payment failed again.' : 'Still processing…');
      if (final === 'COMPLETED') setTimeout(() => { onRetried?.(); }, 1400);
    } catch (e) {
      setRetryStatus('Could not start the payment. Check the number and try again.');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="payment-receipt-modal">
      <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Payment receipt</h3>
          <button onClick={onClose} data-testid="receipt-close" className="p-1.5 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="text-center mb-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${STATUS_STYLE[item.status] || 'text-gray-600 bg-gray-100'}`}>
            {item.status === 'COMPLETED' && <CheckCircle className="w-4 h-4" />}
            {item.status || '—'}
          </span>
          <p className="text-3xl font-extrabold text-gray-900 mt-2 tabular-nums">{fmt(item.amount)}</p>
          <p className="text-xs text-gray-500">{isPayout ? 'Cash-out to mobile money' : 'Mobile money payment'}</p>
        </div>

        <div className="bg-gray-50 rounded-2xl px-4 py-1">
          <ReceiptRow label="Provider" value={item.provider === 'TNM_MWI' ? 'TNM Mpamba' : item.provider === 'AIRTEL_MWI' ? 'Airtel Money' : item.provider || '—'} />
          <ReceiptRow label="Phone" value={item.phoneNumber || '—'} />
          <ReceiptRow label="Date" value={item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'} />
          {item.providerTransactionId && <ReceiptRow label="Transaction ID" value={item.providerTransactionId} />}
          <ReceiptRow label="Reference" value={item.id} />
          {item.ride_id && <ReceiptRow label="Ride" value={item.ride_id} />}
        </div>

        {canRetry && (
          <button
            onClick={retry}
            disabled={retrying}
            data-testid="receipt-retry-btn"
            className="w-full mt-4 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
          >
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            {retrying ? 'Processing…' : 'Retry payment'}
          </button>
        )}
        {retryStatus && <p className="text-[12px] text-center text-gray-600 mt-2" data-testid="receipt-retry-status">{retryStatus}</p>}

        {/* Save / share receipt */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            onClick={downloadPdf}
            data-testid="receipt-download-btn"
            className="h-11 rounded-full bg-gray-900 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" /> Save PDF
          </button>
          <button
            onClick={sharePdf}
            data-testid="receipt-share-btn"
            className="h-11 rounded-full border border-gray-300 text-gray-800 font-semibold text-sm inline-flex items-center justify-center gap-2"
          >
            Share
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Reusable PawaPay payment history list with tappable receipts and retry.
 * Pass either userId (rider deposits) or driverId (driver payouts).
 */
const PawaPayHistory = ({ userId, driverId, title = 'Payment history', compact = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!userId && !driverId) { setLoading(false); return; }
    setLoading(true);
    try {
      const qs = userId ? `user_id=${userId}` : `driver_id=${driverId}`;
      const res = await fetch(`${API}/api/pawapay/history?${qs}&limit=50`);
      const data = await res.json();
      if (data?.success) setItems(data.items || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [userId, driverId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-gray-400" data-testid="pawapay-history-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="text-center py-6 text-sm text-gray-400" data-testid="pawapay-history-empty">
        No mobile-money payments yet.
      </div>
    );
  }

  return (
    <div data-testid="pawapay-history">
      {title && <p className="text-[13px] font-semibold text-gray-900 mb-2">{title}</p>}
      <ul className="space-y-2">
        {items.map((it) => {
          const isPayout = it.type === 'payout';
          const badge = STATUS_STYLE[it.status] || 'text-gray-600 bg-gray-100';
          const failed = FAILED_STATES.includes(it.status);
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => setSelected(it)}
                data-testid="pawapay-history-item"
                className="w-full flex items-center gap-3 bg-white rounded-2xl p-3 border border-gray-100 hover:border-emerald-200 active:scale-[0.99] transition-all text-left"
              >
                <span className={`w-9 h-9 rounded-full flex items-center justify-center ${isPayout ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                  {isPayout ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownLeft className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-gray-900">
                      {isPayout ? 'Cash-out' : 'Payment'} · {it.provider === 'TNM_MWI' ? 'TNM' : it.provider === 'AIRTEL_MWI' ? 'Airtel' : it.provider || ''}
                    </p>
                    <span className="text-[13px] font-bold text-gray-900 tabular-nums">{fmt(it.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badge}`}>{it.status || '—'}{failed ? ' · tap to retry' : ''}</span>
                    <span className="text-[10px] text-gray-400">
                      {it.createdAt ? new Date(it.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                  {!compact && it.providerTransactionId && (
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate">Txn: {it.providerTransactionId}</p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {selected && (
        <ReceiptModal
          item={selected}
          onClose={() => setSelected(null)}
          onRetried={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
};

export default PawaPayHistory;
